import React, {useCallback, useState} from 'react';
import styles from './PreScoringView.module.scss';
import {cn} from '../../../../../../../../../utils';
import {createBorrowingOnboardingManager} from '../../../../../../../../../di';
import {PrescoreRequest} from '../../../../../../../../../api-client';
import {useErrorCallback} from '../../../../../../../../../hooks';
import {Button, Image, InnAutocomplete} from '../../../../../../../../../components';
import {Loader} from '../../../../../../../../../components';
import {useWizardContext} from '../../../../../context';
import stepApprovedIcon from 'assets/images/svg/step-approved.svg';

const MAX_DEBITORS = 10;
const PRESCORE_DELAY_MS = 10000;

const INN_DIGITS_ONLY = /^\d+$/;
const isValidInn = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return INN_DIGITS_ONLY.test(trimmed) && (trimmed.length === 10 || trimmed.length === 12);
};

const PreScoringView = () => {
  const {prescoreDraft, setPrescoreDraft, lastPrescoreResult, setLastPrescoreResult, refreshOnboardingState} = useWizardContext();
  const borrowerInn = prescoreDraft.borrowerInn;
  const debitorInns = prescoreDraft.debitorInns.length > 0 ? prescoreDraft.debitorInns : [''];
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [innFormatError, setInnFormatError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [borrowerRejected, setBorrowerRejected] = useState(false);
  const handleError = useErrorCallback();

  const invalidDebitorInns =
    lastPrescoreResult?.debitors?.filter((d) => !d.isValid).map((d) => d.inn) ?? [];
  const showBorrowerValid = lastPrescoreResult?.borrower?.isValid === true;
  const isBorrowerRejectedByResult = lastPrescoreResult?.borrower?.isValid === false;
  const isBorrowerRejected = borrowerRejected || isBorrowerRejectedByResult;
  const hasInvalidDebitorsFromResult =
    (lastPrescoreResult?.debitors?.some((d) => !d.isValid)) ?? false;

  const setBorrowerInn = useCallback(
    (value: string) => {
      setPrescoreDraft({...prescoreDraft, borrowerInn: value});
      setLastPrescoreResult(null);
      setSubmitError(null);
    },
    [prescoreDraft, setPrescoreDraft, setLastPrescoreResult]
  );

  const addDebitorField = useCallback(() => {
    if (debitorInns.length >= MAX_DEBITORS) return;
    setPrescoreDraft({
      ...prescoreDraft,
      debitorInns: [...debitorInns, ''],
    });
    setSubmitError(null);
    setInnFormatError(null);
    setLastPrescoreResult(null);
  }, [debitorInns.length, prescoreDraft, setPrescoreDraft, setLastPrescoreResult]);

  const updateDebitorInn = useCallback(
    (index: number, value: string) => {
      const next = [...debitorInns];
      next[index] = value;
      setPrescoreDraft({...prescoreDraft, debitorInns: next});
      setSubmitError(null);
      setInnFormatError(null);
      setLastPrescoreResult(null);
    },
    [debitorInns, prescoreDraft, setPrescoreDraft, setLastPrescoreResult]
  );

  const debitorValues = debitorInns.map((s) => s.trim()).filter(Boolean);
  const borrowerTrimmed = borrowerInn.trim();
  const borrowerValid = isValidInn(borrowerTrimmed);
  const atLeastOneDebitor = debitorValues.length >= 1;
  const allDebitorInnsValid = debitorValues.every(isValidInn);
  const anyBorrowerInvalidFormat =
    borrowerTrimmed.length > 0 && !isValidInn(borrowerTrimmed);
  const anyDebitorInvalidFormat = debitorInns.some(
    (s) => s.trim().length > 0 && !isValidInn(s.trim())
  );
  const hasInvalidInnFormat = anyBorrowerInvalidFormat || anyDebitorInvalidFormat;

  const duplicateDebitorInns = (() => {
    const counts: Record<string, number> = {};
    for (const v of debitorValues) {
      counts[v] = (counts[v] ?? 0) + 1;
    }
    return new Set<string>(Object.keys(counts).filter((inn) => counts[inn] > 1));
  })();
  const hasDuplicateDebitorInns = duplicateDebitorInns.size > 0;

  const hasBorrowerInnAmongDebitors =
    borrowerTrimmed.length > 0 && debitorValues.includes(borrowerTrimmed);

  const canSubmit =
    borrowerValid &&
    atLeastOneDebitor &&
    allDebitorInnsValid &&
    !hasInvalidInnFormat &&
    !hasInvalidDebitorsFromResult &&
    !hasDuplicateDebitorInns &&
    !hasBorrowerInnAmongDebitors;

  const updateInnFormatError = useCallback(() => {
    if (anyBorrowerInvalidFormat || anyDebitorInvalidFormat) {
      setInnFormatError('ИНН должен содержать 10 или 12 цифр');
    } else {
      setInnFormatError(null);
    }
  }, [anyBorrowerInvalidFormat, anyDebitorInvalidFormat]);

  const handleFillNewForm = useCallback(() => {
    setBorrowerRejected(false);
    setPrescoreDraft({borrowerInn: '', debitorInns: ['']});
    setSubmitError(null);
    setInnFormatError(null);
    setLastPrescoreResult(null);
  }, [setPrescoreDraft, setLastPrescoreResult]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (!borrowerTrimmed || !atLeastOneDebitor) {
        setSubmitError(
          'Заполните ИНН компании-заемщика и хотя бы один ИНН дебитора'
        );
        return;
      }
      if (!borrowerValid) {
        setSubmitError('ИНН компании-заемщика должен содержать 10 или 12 цифр');
        return;
      }
      if (!allDebitorInnsValid) {
        setSubmitError('Каждый ИНН дебитора должен содержать 10 или 12 цифр');
        return;
      }
      if (hasDuplicateDebitorInns) {
        setSubmitError('Не может быть двух и более одинаковых ИНН дебиторов');
        return;
      }
      if (hasBorrowerInnAmongDebitors) {
        setSubmitError('ИНН заемщика и ИНН дебитора не должны совпадать');
        return;
      }

      const request: PrescoreRequest = {
        borrowerInn: borrowerTrimmed,
        debitorsInns: debitorValues,
      };

      setSubmitting(true);
      setBorrowerRejected(false);
      try {
        await new Promise((resolve) => setTimeout(resolve, PRESCORE_DELAY_MS));
        const manager = await createBorrowingOnboardingManager();
        const result = await manager.prescore(request);
        setLastPrescoreResult(result);
        if (result.borrower?.isValid === false) {
          setBorrowerRejected(true);
        } else {
          setBorrowerRejected(false);
        }
        await refreshOnboardingState({silent: true, keepCurrentStep: true});
      } catch (err: unknown) {
        await handleError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setSubmitting(false);
      }
    },
    [
      borrowerTrimmed,
      borrowerValid,
      atLeastOneDebitor,
      debitorValues,
      allDebitorInnsValid,
      hasDuplicateDebitorInns,
      hasBorrowerInnAmongDebitors,
      handleError,
      refreshOnboardingState,
    ]
  );

  if (submitting) {
    return (
      <div className={styles.preScoringView}>
        <h1 className={styles.title}>
          Pre-scoring заявка на привлечение финансирования
        </h1>
        <div className={styles.loaderBlock}>
          <Loader size="large" overlay={false} />
          <p className={styles.loaderText}>Обработка анкеты, пожалуйста, ожидайте</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.preScoringView}>
      <h1 className={styles.title}>
        Pre-scoring заявка на привлечение финансирования
      </h1>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.sectionTitle}>Данные Вашей компании</div>
        <div className={styles.inputWithIcon}>
          <InnAutocomplete
            type="text"
            inputMode="numeric"
            placeholder="ИНН компании-заемщика"
            className={cn(
              styles.input,
              anyBorrowerInvalidFormat && styles.inputInvalid,
              isBorrowerRejected && styles.inputRejected
            )}
            value={borrowerInn}
            onChange={(v) => {
              setBorrowerInn(v);
              setSubmitError(null);
              setInnFormatError(null);
              setBorrowerRejected(false);
            }}
            onBlur={updateInnFormatError}
            aria-invalid={anyBorrowerInvalidFormat || isBorrowerRejected ? 'true' : undefined}
          />
          {showBorrowerValid && (
            <span className={styles.inputIconValid} aria-hidden>
              <Image src={stepApprovedIcon} alt="" className={styles.inputIconImg} />
            </span>
          )}
        </div>

        {isBorrowerRejected && (
          <div className={styles.rejectionCard} role="alert">
            <div className={styles.rejectionCardInner}>
              <div className={styles.rejectionIcon} aria-hidden>
                <span className={styles.rejectionIconX}>×</span>
              </div>
              <div className={styles.rejectionContent}>
                <h2 className={styles.rejectionTitle}>
                  Ваша pre-scoring заявка отклонена
                </h2>
                <p className={styles.rejectionText}>
                  {lastPrescoreResult?.borrower?.description?.trim() ||
                    'К сожалению, мы не готовы рассмотреть предложенную Вами компанию в качестве заемщика'}
                </p>
                <button
                  type="button"
                  className={styles.rejectionButton}
                  onClick={handleFillNewForm}
                >
                  Заполнить новую анкету
                </button>
              </div>
            </div>
          </div>
        )}

        {!isBorrowerRejected && (
          <>
            <div className={styles.debitorsSection}>
              <div className={styles.sectionTitle}>
                Дебиторы, право требования к которым может выступать обеспечением
              </div>
              {debitorInns.map((value, index) => {
                const trimmed = value.trim();
                const isDebitorInvalidFormat =
                  trimmed.length > 0 && !isValidInn(trimmed);
                const isDebitorInvalidFromResult =
                  trimmed.length > 0 && invalidDebitorInns.includes(trimmed);
                const isDebitorDuplicate =
                  trimmed.length > 0 && duplicateDebitorInns.has(trimmed);
                const isDebitorInvalid =
                  isDebitorInvalidFormat ||
                  isDebitorInvalidFromResult ||
                  isDebitorDuplicate;
                const debitorCheck = lastPrescoreResult?.debitors?.find(
                  (d) => d.inn === trimmed
                );
                const debitorRejectionReason =
                  isDebitorInvalidFromResult && debitorCheck?.description?.trim();
                return (
                  <div key={index} className={styles.inputWithIcon}>
                    <InnAutocomplete
                      type="text"
                      inputMode="numeric"
                      placeholder="ИНН дебитора"
                      className={cn(
                        styles.input,
                        isDebitorInvalidFormat && styles.inputInvalid,
                        isDebitorInvalidFromResult && styles.inputRejected,
                        isDebitorDuplicate && styles.inputInvalid
                      )}
                      value={value}
                      onChange={(v) => updateDebitorInn(index, v)}
                      onBlur={updateInnFormatError}
                      aria-invalid={isDebitorInvalid ? 'true' : undefined}
                    />
                    {isDebitorDuplicate && (
                      <p className={styles.checkReason} role="alert">
                        ИНН дебитора не должен повторяться
                      </p>
                    )}
                    {debitorRejectionReason && !isDebitorDuplicate && (
                      <p className={styles.checkReason} role="alert">
                        {debitorRejectionReason}
                      </p>
                    )}
                  </div>
                );
              })}
              {debitorInns.length < MAX_DEBITORS ? (
                <button
                  type="button"
                  className={styles.addFieldButton}
                  onClick={addDebitorField}
                >
                  <span>+</span>
                  <span>Добавить поле ввода</span>
                </button>
              ) : (
                <p className={styles.maxDebitorsMessage}>
                  Максимальное количество дебиторов {MAX_DEBITORS}
                </p>
              )}
            </div>

            {innFormatError && (
              <p className={styles.errorMessage} role="alert">
                {innFormatError}
              </p>
            )}
            {hasDuplicateDebitorInns && (
              <p className={styles.errorMessage} role="alert">
                Не может быть двух и более одинаковых ИНН дебиторов
              </p>
            )}
            {hasBorrowerInnAmongDebitors && (
              <p className={styles.errorMessage} role="alert">
                ИНН заемщика и ИНН дебитора не должны совпадать
              </p>
            )}
            {submitError && (
              <p className={styles.errorMessage} role="alert">
                {submitError}
              </p>
            )}

            <Button
              type="submit"
              className={styles.submitButton}
              disabled={!canSubmit || submitting}
            >
              {submitting ? 'Отправка…' : 'Отправить'}
            </Button>
            <p className={styles.footerNote}>Ответ будет в течение 1 минуты</p>
          </>
        )}
      </form>
    </div>
  );
};

export default PreScoringView;
